import React, { useEffect, useState } from 'react';
import { Input } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { Sede } from '../../../../models/Entities/Turnero';
import { ErrorType } from '../../../../models/HttpError';
import { SedeFormValues, buildSedePayload, sedeToFormValues, validateSedeForm } from '../Offices.Functions';
import { ErrorBanner } from '../OfficesCommon/OfficesCommon.Component';
import { GuideDrawer, GuideDrawerForm } from '../../../../theme/components/GuideDrawer';
import { GuideField } from '../../../../theme/components/GuideField';

export interface SedeDrawerProps {
  open: boolean;
  sede?: Sede;
  sedes: Sede[];
  isSaving: boolean;
  saveError?: ErrorType;
  onSubmit: (payload: Sede) => void;
  onClose: () => void;
}

const SedeDrawer = ({ open, sede, sedes, isSaving, saveError, onSubmit, onClose }: SedeDrawerProps) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<SedeFormValues>(sedeToFormValues(sede));
  const [showErrors, setShowErrors] = useState(false);
  const editing = sede?.id !== undefined;

  // Se hidrata sólo al abrir o al cambiar de sede: un refetch no pisa lo que se está escribiendo.
  useEffect(() => {
    if (open) {
      setValues(sedeToFormValues(sede));
      setShowErrors(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sede?.id]);

  const errors = validateSedeForm(values, sedes, sede?.id);

  const submit = (event?: React.FormEvent) => {
    event?.preventDefault();
    setShowErrors(true);
    if (Object.keys(errors).length > 0) return;
    onSubmit(buildSedePayload(values, sede?.id));
  };

  return (
    <GuideDrawer
      open={open}
      size="small"
      title={editing ? t('Offices.sede.editTitle') : t('Offices.sede.createTitle')}
      onDismiss={onClose}
      preventDismiss={isSaving}
      closeLabel={t('Offices.actions.close')}
      testId="sede-drawer"
      secondaryAction={{ label: t('Offices.actions.cancel') }}
      primaryAction={{ label: isSaving ? t('Offices.actions.saving') : t('Offices.actions.save'), onClick: () => submit(), loading: isSaving }}
    >
      <GuideDrawerForm id="sede-form" onSubmit={submit}>
        {saveError && <ErrorBanner title={t('Offices.messages.saveError')} error={saveError} testId="sede-save-error" />}
        <GuideField label={t('Offices.sede.name')} required error={showErrors && errors.name ? t(errors.name) : undefined}>
          <Input
            value={values.name}
            maxLength={200}
            placeholder={t('Offices.sede.namePlaceholder')}
            onChange={(_, d) => setValues((v) => ({ ...v, name: d.value }))}
          />
        </GuideField>
        <GuideField label={t('Offices.sede.address')} hint={t('Offices.sede.addressHint')}>
          <Input
            value={values.direccion}
            maxLength={300}
            placeholder={t('Offices.sede.addressPlaceholder')}
            onChange={(_, d) => setValues((v) => ({ ...v, direccion: d.value }))}
          />
        </GuideField>
      </GuideDrawerForm>
    </GuideDrawer>
  );
};

export default SedeDrawer;
